/**
 * Animations module.
 *
 * Scroll-triggered animation support. Any element with `.has-animation` is
 * observed; when it enters the viewport, the `.is-in-view` class is added
 * and the observer is detached (one-shot). The `.has-animation` utility in
 * utility.css pauses whatever animation the element already has until the
 * class flips, so any animation utility (.slide-in, future .fade-in, etc.)
 * composes automatically.
 *
 * Usage:
 *   <span class="slide-in has-animation">Reveals on scroll</span>
 *
 * Door left open for other animation concerns (staggers, parallax, scroll-
 * driven keyframes, etc.) — they can live alongside the observer here.
 */

let animateObserver;

const handleIntersection = (entries, observer) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.add('is-in-view');
            observer.unobserve(entry.target);
        }
    });
};

const initAnimations = () => {
    document.addEventListener('DOMContentLoaded', () => {
        const elements = document.querySelectorAll('.has-animation');

        // Fallback for browsers without IntersectionObserver: flip everything on.
        if (!('IntersectionObserver' in window)) {
            elements.forEach(el => el.classList.add('is-in-view'));
            return;
        }

        if (!animateObserver) {
            // Negative bottom rootMargin shrinks the intersection zone upward,
            // so the trigger fires only once the element is ~25% of a viewport
            // height past its first-visible point.
            animateObserver = new IntersectionObserver(handleIntersection, {
                rootMargin: '0px 0px -25% 0px',
                threshold: 0,
            });
        }

        elements.forEach(el => animateObserver.observe(el));
    });
};

export { initAnimations };
